<!-- Form input for a date with segmented text entry and a calendar picker popup. -->

<script lang="ts" setup>
import type { CalendarDate } from '@internationalized/date';
import { CalendarDate as CalendarDateClass, fromDate, getLocalTimeZone } from '@internationalized/date';
import type { FormFieldController } from '@nzyme/vue-forms/components/defineFormField.js';
import { useDateInput } from '@nzyme/vue-forms/components/useDateInput.jsx';
import { useLanguage } from '@nzyme/vue-i18n/useLanguage.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import type { Slots } from '@nzyme/vue-utils/slots.js';
import { CalendarIcon } from 'lucide-vue-next';
import { DatePickerField, DatePickerInput, DatePickerRoot, DatePickerTrigger } from 'reka-ui';
import { computed, ref } from 'vue';

import DatePickerCalendarContent from '../DatePickerCalendarContent.vue';
import * as l from './DateInput.loc.js';
import FormFieldWrapper from './FormFieldWrapper.vue';

const props = defineProps({
  ...useDateInput.props,
});

defineEmits(useDateInput.emits);
defineSlots<Slots<{ info: { field: FormFieldController<Date | null> } }>>();

const lang = useLanguage();
const t = useTranslate();
const { field } = useDateInput();
const isOpen = ref<boolean>(false);

const dateToCalendarDate = (date: Date): CalendarDate => {
  const zonedDateTime = fromDate(date, getLocalTimeZone());
  return new CalendarDateClass(zonedDateTime.year, zonedDateTime.month, zonedDateTime.day);
};

const calendarDateToDate = (calendarDate: CalendarDate): Date => {
  return calendarDate.toDate(getLocalTimeZone());
};

const calendarValue = computed<CalendarDate | undefined>({
  get() {
    return field.value ? dateToCalendarDate(field.value) : undefined;
  },
  set(value) {
    field.value = value ? calendarDateToDate(value) : null;
  },
});

const normalizedMinValue = computed<CalendarDate | undefined>(() => {
  return props.min ? dateToCalendarDate(props.min) : undefined;
});

const normalizedMaxValue = computed<CalendarDate | undefined>(() => {
  return props.max ? dateToCalendarDate(props.max) : undefined;
});
</script>

<template>
  <FormFieldWrapper
    :field="field"
    :label="label"
    :required="required"
    :disabled="disabled"
    :show-tick="false"
    :inner-class="css.borderless"
  >
    <template #default>
      <DatePickerRoot
        v-model="calendarValue"
        :open="isOpen"
        :disabled="disabled"
        :min-value="normalizedMinValue"
        :max-value="normalizedMaxValue"
        :locale="lang"
        :class="css.datePicker"
        @update:open="isOpen = $event"
      >
        <div :class="css.dateField">
          <DatePickerField
            v-slot="{ segments }"
            :class="css.segmentedInput"
          >
            <template
              v-for="item in segments"
              :key="item.part"
            >
              <DatePickerInput
                v-if="item.part === 'literal'"
                :part="item.part"
                :class="css.separator"
              >
                .
              </DatePickerInput>
              <DatePickerInput
                v-else
                :part="item.part"
                :class="css.segment"
              >
                {{ item.value }}
              </DatePickerInput>
            </template>
            <DatePickerTrigger
              :class="css.calendarTrigger"
              :title="t(l.openCalendar)"
            >
              <CalendarIcon :class="css.calendarIcon" />
            </DatePickerTrigger>
          </DatePickerField>
        </div>
        <DatePickerCalendarContent />
      </DatePickerRoot>
    </template>

    <template #info>
      <slot
        name="info"
        :field="field"
      />
    </template>
  </FormFieldWrapper>
</template>

<style lang="scss" module="css">
.datePicker {
  width: 100%;
}

.dateField {
  display: flex;
  align-items: center;
  gap: $spacing-08;
  min-width: 200px;
  width: 100%;
}

.segmentedInput {
  display: flex;
  align-items: center;
  gap: $spacing-04;
  flex: 1;
  font-size: $font-size-16;
  line-height: $input-height;
}

.segment {
  flex: 1;
  height: $input-height;
  border-radius: $border-radius-08;
  background: $color-white;
  color: $color-gray-800;
  text-align: center;

  &[data-disabled] {
    background: $color-gray-100;
    color: $color-gray-600;
    border-color: $color-gray-500;
  }

  &::placeholder {
    color: $color-gray-600;
  }
}

.separator {
  color: $color-gray-600;
  font-weight: $font-weight-500;
  user-select: none;
  padding: 0 $spacing-04;
}

.calendarTrigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: $spacing-40;
  height: $input-height;
  background: transparent;
  border-radius: $border-radius-08;

  cursor: pointer;
  color: $color-gray-600;

  &:hover {
    background: $color-gray-100;
  }
}
.segment:focus-visible,
.calendarTrigger:focus-visible {
  @include input-focus-outline;
  outline-offset: -2px;
}
</style>
